import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Building2, Sparkles, ArrowRight, XCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, api: authedApi, loading: authLoading } = useAuth();
  const [validating, setValidating] = useState(true);
  const [invite, setInvite] = useState(null); // { valid, email, role, organization, reason? }
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/invites/${token}`);
        if (!cancelled) setInvite(res.data);
      } catch {
        if (!cancelled) setInvite({ valid: false, reason: 'invalid' });
      } finally {
        if (!cancelled) setValidating(false);
      }
    };
    fetchInvite();
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const res = await authedApi.post(`/invites/${token}/accept`);
      setAccepted(true);
      toast.success(res.data.message || 'Joined organization');
      if (res.data.is_client_portal) {
        // Full reload so AuthContext re-fetches /auth/me and picks up the
        // is_client_portal flag before landing on the portal.
        setTimeout(() => { window.location.href = '/portal'; }, 1000);
        return;
      }
      const orgId = res.data.organization?.id || res.data.organization_id;
      setTimeout(() => navigate(orgId ? `/organizations/${orgId}` : '/organizations'), 1200);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  if (validating || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="invite-accept-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">PostToSocial</span>
          </Link>
        </div>

        <Card className="border-zinc-200 shadow-sm">
          {!invite?.valid ? (
            <CardContent className="pt-6 pb-6 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-zinc-900" data-testid="invite-invalid-title">
                {invite?.reason === 'expired' ? 'This invite has expired' : 'This invite is no longer valid'}
              </h2>
              <p className="text-sm text-zinc-600 mt-2">
                Ask the organization owner to send you a new invitation.
              </p>
              <Button className="mt-6" onClick={() => navigate('/dashboard')}>
                Go to dashboard
              </Button>
            </CardContent>
          ) : accepted ? (
            <CardContent className="pt-6 pb-6 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-zinc-900">Welcome aboard!</h2>
              <p className="text-sm text-zinc-600 mt-2">Redirecting you to {invite.organization?.name}...</p>
            </CardContent>
          ) : (
            <>
              <CardHeader className="text-center">
                <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center mx-auto mb-2">
                  <Building2 className="w-6 h-6 text-indigo-600" />
                </div>
                <CardTitle data-testid="invite-org-name">
                  Join {invite.organization?.name}
                </CardTitle>
                <CardDescription>
                  You've been invited as a <span className="font-medium capitalize">{invite.role}</span>.
                  {invite.organization?.description ? <> · {invite.organization.description}</> : null}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!user ? (
                  <div className="space-y-3">
                    <p className="text-sm text-zinc-600 text-center">
                      Sign in as <span className="font-medium">{invite.email}</span> to accept this invitation.
                    </p>
                    <Button
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => navigate(`/login?redirect=/invites/${token}`)}
                      data-testid="invite-signin-btn"
                    >
                      Sign in to accept <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <p className="text-xs text-zinc-500 text-center">
                      Don't have an account?{' '}
                      <Link
                        to={`/register?email=${encodeURIComponent(invite.email)}&redirect=/invites/${token}`}
                        className="text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Create one
                      </Link>
                    </p>
                  </div>
                ) : (user.email || '').toLowerCase() !== invite.email.toLowerCase() ? (
                  <div className="text-center space-y-3">
                    <p className="text-sm text-zinc-600">
                      This invite was sent to <span className="font-medium">{invite.email}</span>,
                      but you're signed in as <span className="font-medium">{user.email}</span>.
                    </p>
                    <Button variant="outline" onClick={() => navigate('/dashboard')}>
                      Go to dashboard
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    onClick={accept}
                    disabled={accepting}
                    data-testid="invite-accept-btn"
                  >
                    {accepting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Accept invitation
                  </Button>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
